import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, TrendingUp, CreditCard, RefreshCcw, Repeat } from "lucide-react";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import { KpiCard } from "@/components/common/KpiCard";
import { GlassCard } from "@/components/common/GlassCard";
import { KpiSkeletonGrid, ChartSkeleton, TableSkeleton } from "@/components/common/Skeletons";
import { StatusBadge } from "@/components/common/StatusBadge";
import { PageTransition } from "@/components/common/PageTransition";
import { ChartTooltip } from "@/components/common/ChartTooltip";
import { usePageHeading } from "@/components/layout/AppLayout";
import { revenueApi } from "@/lib/api";
import { formatCurrency, formatNumber, formatDate } from "@/lib/format";
import { TID } from "@/constants/testIds";

export default function Revenue() {
  usePageHeading("Revenue & Subscriptions", "Financial oversight");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const response = await revenueApi.overview();
        const normalized = {
          totalRevenue: response?.summary?.totalRevenue ?? response?.revenue ?? 0,
          mrr: response?.summary?.mrr ?? response?.mrr ?? 0,
          arr: response?.summary?.arr ?? response?.arr ?? 0,
          arpu: response?.summary?.avgTransaction ?? response?.avgTransaction ?? 0,
          monthly: (response?.chartData ?? response?.trend ?? []).map((item) => ({
            month: item.month ?? item.name ?? item.date ?? "",
            revenue: item.revenue ?? item.amount ?? 0,
          })),
          byPlan: (response?.planBreakdown ?? response?.revenueByPlan ?? []).map((item) => ({
            plan: item.name ?? item.plan ?? "Unknown",
            revenue: item.revenue ?? item.value ?? 0,
            color: item.color ?? "#D4AF37",
          })),
          recentTransactions: (response?.recentTransactions ?? []).map((t) => ({
            id: t.id ?? `${t.clinicName}-${t.date}`,
            clinic: t.clinicName ?? "Unknown",
            plan: t.planName ?? t.plan ?? "Unknown",
            amount: t.amount ?? 0,
            status: (t.status ?? "completed").toLowerCase(),
            date: t.date ?? new Date().toISOString(),
          })),
        };
        setData(normalized);
      } catch {
        toast.error("Failed to load revenue data");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <PageTransition className="space-y-8">
      <div data-testid={TID.revenuePage} />
      {loading || !data ? (
        <KpiSkeletonGrid />
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard label="Total Revenue" value={formatCurrency(data.totalRevenue)} delta={data.netRevenueDelta} icon={Wallet} accent="emerald" index={0} />
          <KpiCard label="MRR" value={formatCurrency(data.mrr)} icon={Repeat} accent="gold" index={1} />
          <KpiCard label="ARR" value={formatCurrency(data.arr)} icon={TrendingUp} accent="indigo" index={2} />
          <KpiCard label="ARPU" value={formatCurrency(data.arpu, { decimals: 2 })} icon={CreditCard} accent="emerald" index={3} />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {loading || !data ? (
          <>
            <ChartSkeleton className="lg:col-span-2" />
            <ChartSkeleton />
          </>
        ) : (
          <>
            <GlassCard className="p-6 lg:col-span-2">
              <h3 className="mb-6 font-heading text-lg font-semibold tracking-tight">Revenue Growth</h3>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={data.monthly} margin={{ left: -12, right: 8, top: 8 }}>
                  <defs>
                    <linearGradient id="revArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
                  <Tooltip content={<ChartTooltip formatter={(v) => formatCurrency(v)} />} />
                  <Area type="monotone" dataKey="revenue" stroke="#D4AF37" strokeWidth={2.5} fill="url(#revArea)" />
                </AreaChart>
              </ResponsiveContainer>
            </GlassCard>

            <GlassCard className="p-6">
              <h3 className="mb-6 font-heading text-lg font-semibold tracking-tight">Revenue by Plan</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.byPlan} margin={{ left: -12, right: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="plan" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
                  <Tooltip content={<ChartTooltip formatter={(v) => formatCurrency(v)} />} cursor={{ fill: "hsl(var(--secondary))", opacity: 0.4 }} />
                  <Bar dataKey="revenue" radius={[8, 8, 0, 0]} maxBarSize={56}>
                    {data.byPlan.map((e) => (
                      <Cell key={e.plan} fill={e.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </GlassCard>
          </>
        )}
      </div>

      {/* Recent transactions */}
      {loading || !data ? (
        <TableSkeleton rows={6} cols={5} />
      ) : (
        <GlassCard className="overflow-hidden">
          <div className="border-b border-border/60 p-5">
            <h3 className="font-heading text-lg font-semibold tracking-tight">Recent Transactions</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b border-border/60 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-5 py-3">Clinic</th>
                  <th className="px-5 py-3">Plan</th>
                  <th className="px-5 py-3">Amount</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.recentTransactions.map((t) => (
                  <tr key={t.id} className="border-b border-border/40 last:border-0 hover:bg-secondary/30">
                    <td className="px-5 py-4 font-medium">{t.clinic}</td>
                    <td className="px-5 py-4 text-muted-foreground">{t.plan}</td>
                    <td className="px-5 py-4 font-semibold text-gold">{formatCurrency(t.amount)}</td>
                    <td className="px-5 py-4"><StatusBadge status={t.status} /></td>
                    <td className="px-5 py-4 text-muted-foreground">{formatDate(t.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GlassCard>
      )}
    </PageTransition>
  );
}
